import { Head, Link } from "@inertiajs/react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button, buttonVariants } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { cn } from "../../lib/cn"

type BatchItem = {
  id: number
  line_number: number
  rps_number: string | null
  detected_cnpj: string | null
  issue_date: string | null
  gross_value: number
  net_value: number
  tributos_total: number
  valor_total: number
  tributos_irrf: number
  tributos_pis: number
  tributos_cofins: number
  tributos_csll: number
  tributos_inss: number
  tributos_iss: number
  status: string
  error_message: string | null
  client_name_detected: string | null
  invoice_id: number | null
  emissao_status: string | null
  data_vencimento: string | null
}

type BatchPayload = {
  id: number
  source_filename: string
  company_name: string
  omie_servico_label?: string | null
  status: string
  issuer_code: string | null
  periodo_inicio: string | null
  periodo_fim: string | null
  total_lines: number
  total_imported: number
  total_errors: number
  total_invoices: number
  emitted_invoices_count: number
  ready_for_return: boolean
  return_generated_at: string | null
  items: BatchItem[]
}

type ImportacaoLoteProps = {
  batch: BatchPayload
}

function money(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
}

function statusLabel(status: string) {
  switch (status) {
    case "processando":
      return "Processando"
    case "importado":
      return "Importado"
    case "parcial":
      return "Importado com erros"
    case "pronto_para_exportacao":
      return "Pronto para retorno"
    default:
      return status
  }
}

export default function ImportacaoLote({ batch }: ImportacaoLoteProps) {
  const itensPendentesCliente = batch.items.filter((item) => item.invoice_id == null && item.status === "erro")

  const cnpjsNaoEncontrados = Array.from(
    new Set(
      itensPendentesCliente
        .filter((item) => !!item.detected_cnpj)
        .map((item) => item.detected_cnpj as string)
    )
  )

  return (
    <>
      <Head title={`Lote ${batch.id}`} />
      <AppLayout>
        <div className="w-full space-y-6 px-4 py-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-white">Lote #{batch.id}</h1>
              <p className="text-slate-400">{batch.source_filename}</p>
            </div>
            <div className="flex gap-2">
              <Link href="/notas-fiscais/importacao" className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}>
                Voltar
              </Link>
              <Link
                href={`/notas-fiscais/importacao/${batch.id}`}
                className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}
              >
                Atualizar status
              </Link>
              {batch.ready_for_return ? (
                <a href={`/notas-fiscais/importacao/${batch.id}/exportar-retorno`} className={buttonVariants()}>
                  Exportar arquivo de retorno
                </a>
              ) : (
                <Button type="button" disabled>
                  Aguardando emissão completa
                </Button>
              )}
            </div>
          </div>

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle>Resumo do lote</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
              <div>
                <p className="text-xs text-slate-400">Empresa</p>
                <p className="text-sm text-slate-100">{batch.company_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Status</p>
                <p className="text-sm text-slate-100">{statusLabel(batch.status)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Serviço Omie</p>
                <p className="text-sm text-slate-100">{batch.omie_servico_label || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Código emissor</p>
                <p className="text-sm text-slate-100">{batch.issuer_code || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Período</p>
                <p className="text-sm text-slate-100">
                  {(batch.periodo_inicio || "—") + " a " + (batch.periodo_fim || "—")}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Linhas</p>
                <p className="text-sm text-slate-100">{batch.total_lines}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Importadas</p>
                <p className="text-sm text-emerald-300">{batch.total_imported}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Emitidas</p>
                <p className="text-sm text-sky-300">
                  {batch.emitted_invoices_count}/{batch.total_invoices}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Erros</p>
                <p className="text-sm text-rose-300">{batch.total_errors}</p>
              </div>
            </CardContent>
          </Card>

          {itensPendentesCliente.length > 0 && (
            <Card className="border-amber-800/60 bg-amber-950/20">
              <CardHeader>
                <CardTitle className="text-amber-200">Clientes/CNPJs não encontrados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-amber-100/90">
                  {itensPendentesCliente.length} linha(s) sem cliente identificado. Verifique se os CNPJs abaixo existem no cadastro de clientes.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    href={`/notas-fiscais/importacao/${batch.id}/sincronizar-clientes-faltantes`}
                    method="post"
                    as="button"
                    className={buttonVariants({ size: "sm" })}
                  >
                    Sincronizar clientes faltantes
                  </Link>
                  <p className="text-xs text-amber-200/80">
                    Sincroniza só a última página de clientes na Omie e tenta vincular as linhas com erro (inclui nova tentativa de criação automática quando aplicável). Se ainda faltar cliente, roda sincronização completa uma vez.
                  </p>
                </div>
                <div className="rounded-md border border-amber-800/60 bg-slate-950/50 p-3">
                  {cnpjsNaoEncontrados.length > 0 ? (
                    <p className="font-mono text-xs text-amber-200">{cnpjsNaoEncontrados.join(" · ")}</p>
                  ) : (
                    <p className="text-xs text-amber-200/80">Nenhum CNPJ detectado nas linhas com erro.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle>Itens importados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="min-w-full text-left text-xs">
                  <thead className="border-b border-slate-800 bg-slate-950/60 text-slate-300">
                    <tr>
                      <th className="px-3 py-2.5 font-medium">#</th>
                      <th className="px-3 py-2.5 font-medium">RPS</th>
                      <th className="px-3 py-2.5 font-medium">CNPJ</th>
                      <th className="px-3 py-2.5 font-medium">Cliente</th>
                      <th className="px-3 py-2.5 text-right font-medium">Valor bruto</th>
                      <th className="px-3 py-2.5 text-right font-medium">Tributos</th>
                      <th className="px-3 py-2.5 text-right font-medium">Total</th>
                      <th className="px-3 py-2.5 text-right font-medium">Líquido</th>
                      <th className="px-3 py-2.5 font-medium">Vencimento</th>
                      <th className="px-3 py-2.5 font-medium">Importação</th>
                      <th className="px-3 py-2.5 font-medium">Emissão</th>
                      <th className="px-3 py-2.5 text-right font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.items.map((item) => (
                      <tr key={item.id} className="border-b border-slate-800/80 last:border-b-0">
                        <td className="px-3 py-2.5 text-slate-100">{item.line_number}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 font-mono text-slate-200">{item.rps_number || "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 font-mono text-slate-300">{item.detected_cnpj || "—"}</td>
                        <td className="max-w-56 truncate px-3 py-2.5 text-slate-200" title={item.client_name_detected || ""}>{item.client_name_detected || "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-slate-200">{money(item.gross_value || 0)}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-amber-300" title={`IRRF ${money(item.tributos_irrf)} | PIS ${money(item.tributos_pis)} | COFINS ${money(item.tributos_cofins)} | CSLL ${money(item.tributos_csll)} | INSS ${money(item.tributos_inss)} | ISS ${money(item.tributos_iss)}`}>
                          {money(item.tributos_total || 0)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-emerald-300">{money(item.valor_total || 0)}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-slate-200">{money(item.net_value || 0)}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-200">{item.data_vencimento ? new Date(item.data_vencimento + "T00:00:00").toLocaleDateString("pt-BR") : "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-200">{item.status}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-200">{item.emissao_status || "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right">
                          {item.invoice_id ? (
                            <Link
                              href={`/notas-fiscais/${item.invoice_id}`}
                              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "border-slate-700 bg-transparent text-xs")}
                            >
                              Abrir nota
                            </Link>
                          ) : (
                            <span className="cursor-help text-xs text-rose-300" title={item.error_message || "Sem vínculo de nota"}>Erro ⚠</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    </>
  )
}
